import { useRef, useState, useEffect, useContext, useCallback} from 'react';
import AppContext from '../contexts/AppContext';
import languageMappings from '../languageMappings';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useAudioRecorderStreamable } from '../hooks/useRecordAudioStreamable';
import { AI_BACKEND, PLATFORM, getCurrentEnv } from '../API';
import { endChatRoom, getChatRoomStatus } from '../services/chatRoomService';
import { getUserFromLocalstorage } from '../utils/localStorage';
import { getWelcomeMessagesApi, getTextToSpeechTranslationApi } from '../services/aiBackendService';
import withLeftSideBar from '../hocs/withLeftSideBar';
import AIRoomView from '../components/AIRoomView';
import { showQuestion } from '../utils/msgbox';
import i18n from '../i18n';
import { PATH_ROUTES } from '../utils/pathRoutes';

const AIRoom = (props) => {
  //eslint-disable-next-line
  const env = getCurrentEnv();
  const { history } = useContext(AppContext);
  const chatroomId = props?.location.state?.chatroomId || sessionStorage.getItem('aiChatroomId') || null;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const wsRef = useRef(null);
  const [playingMessageUrl, setPlayingMessageUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [, setIsLoadingTranslation] = useState(false);
  const [, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [lastChunkTime, setLastChunkTime] = useState(0);
  const [isButtonlessMode] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [streaming, setStreaming] = useState(false);
  
  const { pause: pauseRecording, record, resume: resumeRecording, stop: stopRecording } = useAudioRecorderStreamable({
    wsRef,
    isAuthenticated,
    recording,
    audioStream
  });
  const { pause: pausePlay, play, playChunk, resume: resumePlay, stop: stopPlay, isLoading, isPlaying, isFinished, currentTime, duration } = useAudioPlayer();

  const settingsParams = {
    user_language: props?.location.state?.targetLanguage,
    user_gender: props?.location.state?.userGender,
    client_language: props?.location.state?.language,
    client_gender: props?.location.state?.clientGender,
    sample_rate: 16000, // This needs to match the AudioContext sample rate in useRecordAudioStreamable.js
    platform_url: encodeURIComponent(PLATFORM),
    client_dialect: props?.location.state?.languageDialect || null,
    output_mode: 'stream'
  };
  const sessionSettings = (() => {
    return Object.keys(settingsParams).reduce((memo, key) => {
      if (!memo) {
        memo = `?${key}=${settingsParams[key]}`;
      } else {
        memo += `&${key}=${settingsParams[key]}`;
      }
      return memo;
    }, '');
  })();

  const getWebSocketEndpoint = (tokenHashParam, chatroomIdParam) => {
    if (isButtonlessMode) {
      return `${AI_BACKEND.replace('https', 'wss')}/v1/simultaneous-interpretation/app${sessionSettings}${tokenHashParam}${chatroomIdParam}`;
    }
    return `${AI_BACKEND.replace('https', 'wss')}/v1/speech-to-speech-translation${sessionSettings}${tokenHashParam}${chatroomIdParam}`;
  };

  const getWelcomeMessages = async () => {
    const userFromLocaleStorage = getUserFromLocalstorage();
    const welcomeMessages = await getWelcomeMessagesApi({
      sessionSettings,
      token: userFromLocaleStorage.token
    });
    localStorage.setItem('aiTokenHash', welcomeMessages.token_hash);
    addMessages({
      authorLanguage: settingsParams.client_language,
      translatedLanguage: settingsParams.user_language,
      inputText: welcomeMessages.client_text,
      translation: welcomeMessages.user_text,
      inputAudioUrl: welcomeMessages.client_audio_url,
      translationAudioUrl: welcomeMessages.user_audio_url,
      timestamp: new Date(),
    });
    // NEEDED USER ACTION TO PLAY WELCOME MESSAGES     
    if (welcomeMessages.client_audio_url?.length && welcomeMessages.user_audio_url?.length) {
      setPlayingMessageUrl(welcomeMessages.client_audio_url);
      play(welcomeMessages.client_audio_url, () => {
        setPlayingMessageUrl(welcomeMessages.user_audio_url);
        play(welcomeMessages.user_audio_url, () => {
          setPlayingMessageUrl(null);
        });
      });
    }
  };


  useEffect(() => {
    if (!chatroomId) {
      redirectFromChatRoom();
      return undefined;
    }
    if (props?.location.state?.chatroomId) {
      sessionStorage.setItem('aiChatroomId', props.location.state.chatroomId);
    }
    getWelcomeMessages();
    const statusInterval = setInterval(checkStatus, 3000);
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      localStorage.removeItem('aiTokenHash');
      sessionStorage.removeItem('aiChatroomId');
      clearInterval(statusInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeExtraNewlines = text => {
    if (typeof text !== 'string') {
      return text;
    }
    return text.replace(/\n/g, '\n').trim();
  };
  const addMessages = (message) => {
    setMessages(prevMessages => {
      const originalMessage = {
        language: message.authorLanguage,
        text: removeExtraNewlines(message.inputText),
        audioUrl: message.inputAudioUrl,
        timestamp: message.timestamp,
        error: message.error,
      };
      const translatedMessage = {
        language: message.translatedLanguage,
        text: removeExtraNewlines(message.translation),
        audioUrl: message.translationAudioUrl,
        timestamp: message.timestamp,
        error: message.error,
      };
      const positionedMessages = (()=>{
        if (
          message.authorLanguage === languageMappings[settingsParams.client_language] ||
          message.authorLanguage.slice(0, 2) === settingsParams.client_language.slice(0, 2)
        ) {
          return [originalMessage, translatedMessage];
        } else {
          return [translatedMessage, originalMessage];
        }
      })();
      return [...prevMessages, ...positionedMessages];
    });
  };

  // Separate WebSocket connection establishment
  const establishWebSocketConnection = () => {
    const aiTokenHash = localStorage.getItem('aiTokenHash');
    const tokenHashParam = aiTokenHash ? `&token_hash=${aiTokenHash}` : '';
    const chatroomIdParam = `&chatroom_id=${chatroomId}`;

    const wsUrl = getWebSocketEndpoint(tokenHashParam, chatroomIdParam);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';
    const user = getUserFromLocalstorage();
    ws.onopen = () => console.log('WebSocket connected');
    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        const data = JSON.parse(event.data);
        if (data.type === 'AUTH_REQ') {
          ws.send(JSON.stringify({ token: user?.token, }));
        } else if (data.type === 'AUTH_OK') {
          console.log('Received AUTH_RES:', data);
          if (data.status_code === 200) {
            setIsAuthenticated(true);
          } else {
            console.error('Authentication failed', data);
          }
        } else if (data.type === 'translation_results') {

          const content = data.translation_results;
          if (content.translation_audio_url) {
            setPlayingMessageUrl(content.translation_audio_url);
          }
          const message = {
            authorLanguage: content.detected_input_language,
            translatedLanguage: content.translation_language,
            inputText: content.detected_input_transcription_text,
            translation: content.translation_text,
            translationAudioUrl: content.translation_audio_url,
            timestamp: new Date(),
          };

          addMessages(message);
          setIsLoadingTranslation(false);
        } else if (data.type === 'error') {
          const content = data.translated_error;
          setPlayingMessageUrl(content.user_audio_url);
          play(content.user_audio_url, () => {
            setPlayingMessageUrl(content.client_audio_url);
            play(content.client_audio_url, () => {
              setPlayingMessageUrl(null);
            });
          });

          const message = {
            authorLanguage: settingsParams.user_language,
            translatedLanguage: settingsParams.client_language,
            inputText: content.user_text,
            translation: content.client_text,
            translationAudioUrl: content.client_audio_url,
            inputAudioUrl: content.user_audio_url,
            timestamp: new Date(),
            error: true,
          };
          addMessages(message);
          setIsLoadingTranslation(false);
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.close();
            console.log('WebSocket connection closed by client.');
          }
        }
      } else {
        playChunk(event.data);
        setLastChunkTime(Date.now());
      }
    };

    ws.onerror = (err) => console.error('WS error', err);
    ws.onclose = () => console.log('WebSocket closed');

    return () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ event: 'interaction_end' }));
      }
      wsRef.current?.close();
    };
  };

  const onStartRecording = async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'cancel' }));
      wsRef.current.close();
    }
    stopPlay();
    record();
    setStreaming(true);
    setRecording(true);
    
    setError(null);
    establishWebSocketConnection();
  };

  const onPauseRecording = () => {
    pauseRecording();
    setRecording(false);
  };

  const onResumedRecording = () => {
    resumeRecording();
    setRecording(true);
  };

  const onStopRecording = (isClose) => {
    const ws = wsRef.current;
    setRecording(false);
    stopRecording();
    setAudioStream(null);
    if (isClose) {
      if (ws && ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'cancel' }));
      }
      return;
    }

    if (ws && ws?.readyState === WebSocket.OPEN) {
      setIsLoadingTranslation(true);
      // Send a stop signal instead of closing the WebSocket immediately
      ws.send(JSON.stringify({ action: 'stop' }));
      console.log('Sent stop signal to server.');
    }
  };
  const onRecordClick = (close) => {
    if (recording) {
      onStopRecording(close);
    } else {
      onStartRecording();
    }
  };

  const onSend = (message) => {
    stopPlay();
    setIsLoadingTranslation(true);
    const user = getUserFromLocalstorage();
    if (!message || message.trim().length === 0) 
      return;
    getTextToSpeechTranslationApi({
      sessionSettings,
      text: message,
      token: user?.token,
      chatroomId,
    }).then((translatedText) => {
      setIsLoadingTranslation(false);
      if (translatedText.translated_error) {
        addMessages({
          authorLanguage: settingsParams.user_language,
          translatedLanguage: settingsParams.client_language,
          inputText: translatedText.translated_error.user_text,
          translation: translatedText.translated_error.client_text,
          inputAudioUrl: translatedText.translated_error.user_audio_url,
          translationAudioUrl: translatedText.translated_error.client_audio_url,
          timestamp: new Date(),
          error: true,
        });
        setPlayingMessageUrl(translatedText.translated_error.user_audio_url);
        play(translatedText.translated_error.user_audio_url, () => {
          setPlayingMessageUrl(translatedText.translated_error.client_audio_url);
          play(translatedText.translated_error.client_audio_url, () => {
            setPlayingMessageUrl(null);
          });
        });
        return;
      }
      addMessages({
        authorLanguage: translatedText.detected_input_language,
        translatedLanguage: translatedText.translation_language,
        inputText: translatedText.detected_input_transcription_text,
        translation: translatedText.translation_text,
        translationAudioUrl: translatedText.translation_audio_url,
        timestamp: new Date(),
      });
      if (translatedText.translation_audio_url) {
        playMessage(translatedText.translation_audio_url);
      }
    });
  };

  const playMessage = useCallback((audioUrl) => {
    if (playingMessageUrl && isPlaying) {
      !streaming ? pausePlay() : stopPlay();
      setStreaming(false);
      return;
    } 

    
    if (!isPlaying && !isLoading && currentTime > 0 && !isFinished && playingMessageUrl === audioUrl && !streaming) {
      resumePlay();
    } else {
      const latestMessage = messages[messages.length - 1];
      const url = audioUrl || latestMessage.audioUrl;
      stopPlay();
      setPlayingMessageUrl(url);
      play(url, () => { 
        setPlayingMessageUrl(null); 
      });
    }
  },[currentTime, isLoading, isPlaying, messages, pausePlay, play, playingMessageUrl, resumePlay, stopPlay, streaming, isFinished]);

  const stopPlayingMessage = () => {
    stopPlay();
    setPlayingMessageUrl(null);
  };

  function redirectFromChatRoom() {
    const historyState = {
      ai: true, 
      aiMeetingClosed: true,
      chatroomId,
      chatroom: props?.location.state?.chatroom,
    };
    history.replace(PATH_ROUTES.home, historyState);
  }

  async function checkStatus() {
    try {
      const chatRoomStatus = await getChatRoomStatus(chatroomId);
      
      if (chatRoomStatus.closed) {
        console.log('Status from server: CLOSED');

        redirectFromChatRoom();
        return;
      }
      const lastChunkTimeDiff = Date.now() - lastChunkTime;
      if (lastChunkTimeDiff > 1000 && lastChunkTimeDiff < 3000) {
        console.log('No new audio chunks received for 1 seconds, stop playing');
        stopPlayingMessage();
      }
    } catch (e) {
      console.error(e);
    }
  }

  const onClose = async () => {
    const accepted = await showQuestion(i18n('doYouWantToHangUp'));
    if (accepted) {
      await endChatRoom(chatroomId);
      redirectFromChatRoom();
    }
  };

  // Handle audio stream from VoiceVisualizerView
  const handleAudioStreamAvailable = (stream) => {
    setAudioStream(stream);
  };

  return (
    <AIRoomView 
      handleSend={onSend} 
      handleRecordClick={onRecordClick} 
      handlePauseRecording={onPauseRecording}
      handleResumedRecording={onResumedRecording}
      handleClose={onClose} 
      messages={messages} 
      playMessage={playMessage}
      stopPlayingMessage={stopPlayingMessage}
      isPlaying={isPlaying}
      isRecording={recording}
      currentTime={currentTime}
      duration={duration}
      playingMessageUrl={playingMessageUrl}
      onAudioStreamAvailable={handleAudioStreamAvailable}
    />
  );
};

export default withLeftSideBar(AIRoom);